// Lightweight, conversion-focused top nav for public SEO/marketing pages.
// Distinct from GlobalNav (which is auth-only, badge-heavy).
// Goal: keep the page bare for SEO/readability while giving visitors
// one clear path back to the homepage and one clear sign-up CTA.
import React, { useState } from 'react';
import { Heart, Menu, X } from 'lucide-react';
import { ThemeToggleCompact } from './ThemeToggle';
import { trackEvent } from '../api/analytics';
import { stashSeoReferrer } from '../lib/seoReferrer';

const LINKS = [
  { href: '/guides', label: 'Guides' },
  { href: '/reports', label: 'Research' },
  { href: '/about-unitylove', label: 'About' },
  { href: '/how-it-works', label: 'How It Works' },
];

export default function PublicSEOHeader() {
  const [open, setOpen] = useState(false);

  // Fire-and-forget — never blocks navigation
  const handleSignupClick = () => {
    const path = typeof window !== 'undefined' ? window.location.pathname : '';
    stashSeoReferrer(path);
    trackEvent('seo_header_cta_click', { cta: 'create_profile', path });
  };

  const handleSigninClick = () => {
    const path = typeof window !== 'undefined' ? window.location.pathname : '';
    stashSeoReferrer(path);
    trackEvent('seo_header_cta_click', { cta: 'sign_in', path });
  };

  return (
    <header
      className="sticky top-0 z-40 border-b border-slate-200 dark:border-slate-800 bg-white/85 dark:bg-slate-950/85 backdrop-blur supports-[backdrop-filter]:bg-white/65 dark:supports-[backdrop-filter]:bg-slate-950/65"
      data-testid="public-seo-header"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-14 sm:h-16">
          {/* Logo */}
          <a
            href="/"
            className="flex items-center gap-2 hover:opacity-80 transition"
            data-testid="seo-header-logo"
          >
            <Heart className="w-6 h-6 text-rose-600 fill-current" />
            <span className="font-playfair text-lg sm:text-xl font-bold text-slate-900 dark:text-slate-50">
              UnityLove
            </span>
          </a>

          {/* Desktop links */}
          <nav className="hidden md:flex items-center gap-6">
            {LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                className="text-sm font-medium text-slate-700 dark:text-slate-300 hover:text-rose-600 dark:hover:text-rose-400 transition-colors"
              >
                {l.label}
              </a>
            ))}
          </nav>

          {/* Right side CTAs */}
          <div className="flex items-center gap-1 sm:gap-3">
            <div className="hidden sm:block">
              <ThemeToggleCompact />
            </div>
            <a
              href="/auth?mode=login"
              onClick={handleSigninClick}
              className="hidden sm:inline-flex items-center text-sm font-medium text-slate-700 dark:text-slate-200 hover:text-rose-600 dark:hover:text-rose-400 px-3 py-2 transition-colors"
              data-testid="seo-header-signin"
            >
              Sign In
            </a>
            <a
              href="/auth?mode=signup"
              onClick={handleSignupClick}
              className="inline-flex items-center px-4 py-2 rounded-full bg-rose-600 text-white text-sm font-semibold hover:bg-rose-700 active:scale-95 transition-all shadow-sm"
              data-testid="seo-header-signup"
            >
              Create Profile
            </a>
            {/* Mobile menu toggle */}
            <button
              onClick={() => setOpen((v) => !v)}
              className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-lg text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              aria-label="Toggle navigation"
              aria-expanded={open}
              data-testid="seo-header-menu-toggle"
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {open && (
          <nav
            className="md:hidden border-t border-slate-200 dark:border-slate-800 py-2"
            data-testid="seo-header-mobile-menu"
          >
            <ul className="flex flex-col">
              {LINKS.map((l) => (
                <li key={l.href}>
                  <a
                    href={l.href}
                    onClick={() => setOpen(false)}
                    className="block px-2 py-3 text-base font-medium text-slate-800 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800/60 rounded-lg"
                  >
                    {l.label}
                  </a>
                </li>
              ))}
              <li className="border-t border-slate-200 dark:border-slate-800 mt-1 pt-2">
                <a
                  href="/auth?mode=login"
                  onClick={handleSigninClick}
                  className="block px-2 py-3 text-base font-medium text-slate-800 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800/60 rounded-lg"
                >
                  Sign In
                </a>
              </li>
            </ul>
          </nav>
        )}
      </div>
    </header>
  );
}
